import React, {FC, useEffect, useMemo, useState} from 'react';
import {TranslatableAndSpeakableLanguage, TtsVoice} from '../contexts/Configuration';
import {IconButton, Menu, MenuItem, ToggleButton, ToggleButtonGroup} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import {useAppSelector} from '../hooks/storeHooks';

/**
 * Strips the locale prefix from a voice name.
 * e.g. "en-US-JennyNeural" -> "JennyNeural"
 *      "zh-CN-XiaoxiaoNeural" -> "XiaoxiaoNeural"
 *      "Jenny" -> "Jenny" (unchanged when fewer than 3 dash-separated parts)
 */
const formatVoiceName = (voice: TtsVoice): string => {
    const parts = voice.name.split('-');
    const name = parts.length >= 3 ? parts.slice(2).join('-') : voice.name;
    return name.replace('Neural', '');
};

/**
 * Sorts voices so Female voices appear before Male voices.
 * Preserves original order within each gender group.
 */
const sortVoicesByGender = (voices: TtsVoice[]): TtsVoice[] => {
    return [...voices].sort((a, b) => {
        if (a.gender === b.gender) return 0;
        return a.gender === 'F' ? -1 : 1;
    });
};

const VoiceSelector: FC<VoiceSelectorProps> = ({id, language, disabled, onVoiceSelected}) => {

    const preSelectedOutputVoices = useAppSelector((state) => state.session.preSelectedOutputVoices);

    const voices = useMemo(
        () => language ? sortVoicesByGender(language.voices) : [],
        [language]
    );

    const selectedVoice = useMemo<TtsVoice | null>(() => {
        const savedName = preSelectedOutputVoices[id];
        if (savedName) {
            const match = voices.find(v => v.name === savedName);
            if (match) return match;
        }
        return voices.length > 0 ? voices[0] : null;
    }, [voices, preSelectedOutputVoices, id]);

    const selectedGender: 'F' | 'M' = selectedVoice?.gender ?? 'F';

    const hasFemale = useMemo(() => voices.some(v => v.gender === 'F'), [voices]);
    const hasMale = useMemo(() => voices.some(v => v.gender === 'M'), [voices]);

    const voicesForGender = useMemo(
        () => voices.filter(v => v.gender === selectedGender),
        [voices, selectedGender]
    );

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    useEffect(() => {
        if (voices.length > 0) {
            onVoiceSelected(selectedVoice ? selectedVoice.name : null);
        }
    }, [selectedVoice]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGenderChange = (_event: React.MouseEvent<HTMLElement>, newGender: 'F' | 'M' | null) => {
        if (!newGender) return;
        const firstVoiceOfGender = voices.find(v => v.gender === newGender);
        if (firstVoiceOfGender) {
            onVoiceSelected(firstVoiceOfGender.name);
        }
    };

    const isDisabled = disabled || voices.length === 0;

    return (
        <div className="voice-selector-box">
            <div className="voice-selector-label">Select Voice</div>
            <div className="voice-selector-controls">
                <ToggleButtonGroup
                    value={selectedGender}
                    exclusive
                    onChange={handleGenderChange}
                    disabled={isDisabled}
                    size="small"
                    className="voice-gender-toggle"
                >
                    {hasFemale && <ToggleButton value="F" className="voice-gender-btn">Female</ToggleButton>}
                    {hasMale && <ToggleButton value="M" className="voice-gender-btn">Male</ToggleButton>}
                </ToggleButtonGroup>
                <IconButton
                    size="small"
                    disabled={isDisabled}
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    className="voice-gear-btn"
                >
                    <SettingsIcon fontSize="small"/>
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                >
                    {voicesForGender.map((v, index) => (
                        <MenuItem
                            key={v.name}
                            onClick={() => {
                                onVoiceSelected(v.name);
                                setAnchorEl(null);
                            }}
                            className="voice-menu-item"
                        >
                            <span className="voice-menu-name">
                                {formatVoiceName(v)}
                                {index === 0 && <span className="voice-menu-default"> (Default)</span>}
                            </span>
                            <span className="voice-menu-check">
                                {v.name === selectedVoice?.name && <CheckIcon fontSize="inherit"/>}
                            </span>
                        </MenuItem>
                    ))}
                </Menu>
            </div>
        </div>
    );
};

type VoiceSelectorProps = {
    id: string,
    language: TranslatableAndSpeakableLanguage | null,
    disabled: boolean,
    onVoiceSelected: (voice: string | null) => void,
}

export default VoiceSelector;
